import { useCallback, useEffect, useState } from 'react'
import { getAutomationSettings, updateAutomationSettings } from '../services/automationSettingsApi'
import type { AutomationMode, AutomationSettings } from '../types'

// Unlike useQualificationNotificationSettings, GET here is open to every workspace
// member (Automation Settings spec §3: Sales Managers/Reps — mapped onto the
// existing `member` role — see it read-only), so there is no `enabled` gate on the
// fetch itself; only `setMode` calls are meaningful for an owner/admin caller.
export function useAutomationSettings(workspaceId: string) {
  const [settings, setSettings] = useState<AutomationSettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const load = useCallback(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    getAutomationSettings(workspaceId)
      .then((s) => { if (!cancelled) setSettings(s) })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load automation settings') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [workspaceId])

  useEffect(() => load(), [load])

  // Saves immediately on selection, same as qualification-notification-settings —
  // no page-level Save button for this setting.
  const setMode = useCallback(async (actionKey: string, mode: AutomationMode) => {
    const previous = settings
    setSettings((prev) => (prev ? { ...prev, actions: { ...prev.actions, [actionKey]: mode } } : prev))
    setSaveError(null)
    setSaving(true)
    try {
      const resolved = await updateAutomationSettings(workspaceId, actionKey, mode)
      setSettings(resolved)
    } catch (err) {
      setSettings(previous)
      setSaveError(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setSaving(false)
    }
  }, [workspaceId, settings])

  return { settings, loading, error, retry: load, saving, saveError, setMode }
}
